import classNames from "classnames"

import {
  LinkTag,
  WidgetTag,
  isInPlaceEditingActive,
  provideComponent
} from "scrivito"
import { HEADER_HEIGHT_VARIABLE_NAME } from "shared/constants/app-constants"
import { useElementHeightStore } from "shared/lib/css/useElementHeightStore"
import { ensureString } from "shared/lib/ensureString"
import { Alignment } from "shared/lib/layout/Alignment"
import { getAlignmentContainerClassName } from "shared/lib/layout/getAlignmentContainerClassname"
import { scrollWindowTo } from "shared/lib/scrollWindowTo"
import { useEvent } from "shared/lib/useEvent"
import { AppActionType } from "shared/types/AppAction"
import { IconPlacement } from "shared/types/Button"
import { IconSize } from "shared/types/Icon"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { useAppAction } from "app/useAppAction"

import { ButtonWidget } from "./ButtonWidgetClass"

provideComponent(ButtonWidget, ({ widget }) => {
  const performAction = useAppAction()
  const target = widget.get("target")
  const type = widget.get("type") || "link"
  const variant = widget.get("variant")
  const isDanger = widget.get("isDanger")
  const isBold = widget.get("isBold")
  const iconPlacement =
    (widget.get("iconPlacement") as IconPlacement) || IconPlacement.LEFT
  const iconSize = (widget.get("iconSize") as IconSize) || IconSize.TEXT
  const targetTitle = ensureString(target?.title())
  const headerHeight = useElementHeightStore(HEADER_HEIGHT_VARIABLE_NAME)

  const shouldBeBold = isBold || variant === ButtonVariant.PRIMARY

  const handleButtonClick = useEvent(() => {
    performAction(widget.get("action") as AppActionType)
  })

  const handleScrollClick = useEvent(() => {
    const scrollTargetId = widget.get("scrollTargetId")
    if (scrollTargetId) {
      scrollWindowTo(scrollTargetId, {
        scrollBehavior: "smooth",
        offsetY: headerHeight
      })
    }
  })

  const renderButton = () => {
    if (type === "button") {
      return (
        <Button
          variant={variant || undefined}
          isDanger={isDanger}
          label={widget.get("title")}
          aria-label={widget.get("title")}
          onClick={handleButtonClick}
          icon={widget.get("icon") as IconName}
          iconPlacement={iconPlacement}
          iconSize={iconSize}
          className={shouldBeBold ? "btn--bold" : undefined}
        />
      )
    }

    if (type === "scrolltarget") {
      return (
        <Button
          variant={variant || undefined}
          isDanger={isDanger}
          label={widget.get("title")}
          aria-label={widget.get("title")}
          onClick={handleScrollClick}
          icon={widget.get("icon") as IconName}
          iconPlacement={iconPlacement}
          iconSize={iconSize}
          className={shouldBeBold ? "btn--bold" : undefined}
        />
      )
    }

    const icon = widget.get("icon") ? (
      <Icon name={widget.get("icon") as IconName} size={iconSize} />
    ) : null

    const labelContent =
      !targetTitle && isInPlaceEditingActive()
        ? "Provide the button link and text in the widget properties."
        : targetTitle

    return (
      <LinkTag
        to={target}
        className={classNames("btn", "btn-l", `btn-${variant}`, {
          "btn--bold": shouldBeBold
        })}
      >
        {iconPlacement === IconPlacement.LEFT ? icon : null}
        {labelContent}
        {iconPlacement === IconPlacement.RIGHT ? icon : null}
      </LinkTag>
    )
  }

  return (
    <WidgetTag
      className={getAlignmentContainerClassName(
        widget.get("alignment") as Alignment
      )}
    >
      {renderButton()}
    </WidgetTag>
  )
})
